import { CalendarCheckIcon, CheckCircleIcon, LinkBrokenIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { getInitial } from '@orn/shared-web/utils/getInitials';
import * as React from 'react';

import { getAlertEntity, getAlertIconVariant } from '../../utils/alerts';

import type { AlertEntityRefs, AlertIconVariant, AlertRowType } from '../../utils/alerts';

/** The fields the icon classifies on — a subset of the alert row model. */
export type AlertTypeIconCallout = { type: AlertRowType } & AlertEntityRefs;

const TILE_CLASSNAME = 'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border';

const GLYPH_BY_VARIANT: Record<
  Exclude<AlertIconVariant, 'entity'>,
  { icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  update: {
    icon: CheckCircleIcon,
    className: 'bg-utility-success-50 border-utility-success-200 text-utility-success-600',
  },
  reminder: {
    icon: CalendarCheckIcon,
    className: 'bg-utility-blue-light-50 border-utility-blue-light-200 text-utility-blue-light-600',
  },
  system: {
    icon: LinkBrokenIcon,
    className: 'bg-utility-error-50 border-utility-error-200 text-utility-error-600',
  },
};

interface IAlertTypeIconProps {
  callout: AlertTypeIconCallout;
  className?: string;
}

/**
 * Leading 40px visual for an alert row: the logo of the merchant, account, bank,
 * biller, or recurring stream the alert is about, or a tinted glyph standing in
 * for its intent when it is about the household as a whole. Carries no status
 * badge — the row draws that over this tile's corner.
 */
const AlertTypeIcon = ({ callout, className }: IAlertTypeIconProps) => {
  const variant = getAlertIconVariant(callout);

  if (variant === 'entity') {
    const entity = getAlertEntity(callout);

    return (
      <div className={cx(TILE_CLASSNAME, 'bg-primary border-secondary_alt', className)}>
        {entity?.logoUrl ? (
          <img src={entity.logoUrl} alt="" className="size-full object-cover" />
        ) : (
          <span className="text-secondary text-md font-semibold">{getInitial(entity?.name)}</span>
        )}
      </div>
    );
  }

  const { icon: Icon, className: variantClassName } = GLYPH_BY_VARIANT[variant];

  return (
    <div className={cx(TILE_CLASSNAME, variantClassName, className)}>
      <Icon className="size-5 shrink-0" />
    </div>
  );
};

export default AlertTypeIcon;
